import { stringify } from '@tock/query-string';

import { Crop } from 'react-image-crop';
import { FileWithPreview, core } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { BusinessImage, MultiImageRequest, UploadedImage } = protos;

export function uploadImagePublic(file: FileWithPreview, crop?: Crop): ApiAction {
  return _uploadImage(file, '/image/upload/public', crop);
}

export function uploadImageAuth(file: FileWithPreview, crop?: Crop): ApiAction {
  return _uploadImage(file, '/image/upload', crop);
}

export function uploadImageTicketOption(file: FileWithPreview, crop?: Crop): ApiAction {
  return _uploadImage(file, '/ticket/option/image', crop);
}

export function uploadImageMenuItem(file: FileWithPreview, crop?: Crop): ApiAction {
  // Ticket options (i.e. add-ons) and menu items share a handler for images.
  return _uploadImage(file, '/ticket/option/image', crop);
}

function _uploadImage(file: FileWithPreview, path: string, crop?: Crop): ApiAction {
  let apiPath = path;
  if (crop) {
    const { x, y, width, height } = crop;
    apiPath = apiPath + `?x=${x}&y=${y}&width=${width}&height=${height}`;
  }
  return {
    TockApi: {
      postFile: apiPath,
      request: file,
      type: AppConstants.IMAGE_UPLOAD,
    },
  };
}

export function updateImage(image: core.IUploadedImage): ApiAction {
  return {
    TockApi: {
      putProto: `/image/update`,
      request: UploadedImage.fromObject(image),
      type: AppConstants.IMAGE_UPDATE,
    },
  };
}

export function upsertBusinessImage(
  businessImage: core.IBusinessImage,
  imageUse?: string
): ApiAction {
  return {
    TockApi: {
      postProto: `/image/business?${stringify({ imageUse })}`,
      request: BusinessImage.fromObject(businessImage),
      type: AppConstants.IMAGE_UPSERT_BUSINESS_IMAGE,
    },
  };
}

export function archiveBusinessImage(imageId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/image/business/${imageId}/archive`,
      type: AppConstants.IMAGE_DELETE_BUSINESS_IMAGE,
    },
  };
}

export function fetchBusinessImages(): ApiAction {
  return {
    TockApi: {
      getListProto: '/image/business',
      type: AppConstants.IMAGE_FETCH_ALL_BUSINESS_IMAGE,
    },
  };
}

export function addImagesToBeCropped(
  files: FileWithPreview[]
): ReduxActions.Action<FileWithPreview[]> {
  return {
    type: AppConstants.IMAGE_ADD_IMAGES_TO_BE_CROPPED,
    payload: files,
  };
}

export function removeImageToBeCropped(
  file: FileWithPreview
): ReduxActions.Action<FileWithPreview> {
  return {
    type: AppConstants.IMAGE_REMOVE_IMAGE_TO_BE_CROPPED,
    payload: file,
  };
}

export function getBusinessProfileImages(): ApiAction {
  return {
    TockApi: {
      getListProto: '/image/business/profile',
      type: AppConstants.IMAGE_FETCH_ALL_BUSINESS_PROFILE_IMAGES,
    },
  };
}

export function updateBusinessProfileImages(image: core.UploadedImage[]): ApiAction {
  return {
    TockApi: {
      postListProto: '/image/business/profile',
      request: MultiImageRequest.fromObject({ image }),
      type: AppConstants.IMAGE_UPSERT_BUSINESS_PROFILE_IMAGES,
      toast: 'Images updated',
    },
  };
}

export function getTicketTypeImages(id: number): ApiAction {
  return {
    TockApi: {
      getListProto: `/image/ticket-type/${id}`,
      context: { id },
      type: AppConstants.IMAGE_FETCH_ALL_TICKET_TYPE_IMAGES,
    },
  };
}

export function updateTicketTypeImages(id: number, image: core.UploadedImage[]): ApiAction {
  return {
    TockApi: {
      postListProto: `/image/ticket-type/${id}`,
      request: MultiImageRequest.fromObject({ image }),
      context: { id },
      type: AppConstants.IMAGE_UPSERT_TICKET_TYPE_IMAGES,
      toast: 'Images updated',
    },
  };
}
